import { A, useNavigate } from "@solidjs/router";
import { createSignal, For, onCleanup, type ParentProps } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";

const navItems = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/send", label: "Send", end: false },
  { to: "/sub-wallets", label: "Sub-wallets", end: false },
  { to: "/approvals", label: "Approvals", end: false },
  { to: "/history", label: "History", end: false },
  { to: "/buy", label: "Buy", end: false },
];

export function AppShell(props: ParentProps) {
  const { session, client, clearSession } = useAuth();
  const navigate = useNavigate();
  // Below 768px the nav + account strip collapse behind a hamburger (styles.css).
  const [open, setOpen] = createSignal(false);
  const close = () => setOpen(false);

  // The account id is long and hard to read truncated — clicking it copies the
  // full id to the clipboard (with a brief "Copied!" confirmation).
  const [copied, setCopied] = createSignal(false);
  let copyTimer: ReturnType<typeof setTimeout> | undefined;
  function copyAccountId() {
    const id = session()?.accountId;
    if (!id) return;
    void navigator.clipboard
      ?.writeText(id)
      .then(() => {
        setCopied(true);
        clearTimeout(copyTimer);
        copyTimer = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }
  onCleanup(() => clearTimeout(copyTimer));

  function handleLogout() {
    close();
    // Drop the local session first so a hung logout can't keep us signed in.
    clearSession();
    navigate("/login", { replace: true });
    void client.logout().catch(() => {});
  }

  return (
    <div class="app-shell">
      <header class="app-header">
        <div class="brand">GG-Coin</div>
        <button
          type="button"
          class="nav-toggle"
          classList={{ "is-open": open() }}
          aria-label={open() ? "Close menu" : "Open menu"}
          aria-expanded={open()}
          aria-controls="app-nav"
          onClick={() => setOpen(!open())}
        >
          <span class="nav-toggle-bar" />
          <span class="nav-toggle-bar" />
          <span class="nav-toggle-bar" />
        </button>
        <nav
          id="app-nav"
          class="primary-nav"
          classList={{ "is-open": open() }}
          onKeyDown={(e) => e.key === "Escape" && close()}
        >
          <For each={navItems}>
            {(item) => (
              <A
                href={item.to}
                end={item.end}
                class="nav-link"
                activeClass="active"
                onClick={close}
              >
                {item.label}
              </A>
            )}
          </For>
        </nav>
        <div class="account-strip" classList={{ "is-open": open() }}>
          <button
            type="button"
            class="account-id copy-id"
            title={copied() ? "Copied!" : "Click to copy your account id"}
            aria-label="Copy account id"
            onClick={copyAccountId}
          >
            {copied() ? "Copied!" : session() ? `${session()?.accountId.slice(0, 8)}…` : ""}
          </button>
          <button type="button" class="link-button" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </header>
      <main class="app-main">{props.children}</main>
    </div>
  );
}
